import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  createLockerExpansionSession,
  getLockerExpansionSessionStatus,
} from '../../services/api';
import { useAuth } from '../auth';
import { useToast } from '../toast';
import { logLockerExpansion, logLockerShipment } from '../../services/analytics';

const BASE_LOCKER_SLOTS = 8;

const LockerContext = createContext({
  lockerDecision: null,
  lockerGainEvent: null,
  lockerActionPending: false,
  lockerExpansionContext: null,
  shippingFlowContext: null,
  shippingRequestPending: false,
  shippingError: null,
  showLockerFullModal: false,
  setLockerDecision: () => {},
  setLockerGainEvent: () => {},
  expandLocker: async () => {},
  startShippingFlow: () => {},
  shipLockerNow: () => {},
  openLockerFullModal: () => {},
  closeLockerFullModal: () => {},
  resolveLockerItems: () => [],
});

const resolveLockerItems = (account) => {
  if (!account) {
    return [];
  }
  if (Array.isArray(account.locker)) {
    return account.locker;
  }
  if (Array.isArray(account.history)) {
    return account.history;
  }
  return [];
};

const LockerProvider = ({ children }) => {
  const { user, authReady, requireLogin, refreshUser } = useAuth();
  const { showToast } = useToast();
  const [lockerDecision, setLockerDecision] = useState(null);
  const [lockerGainEvent, setLockerGainEvent] = useState(null);
  const [lockerActionPending, setLockerActionPending] = useState(false);
  const [lockerExpansionContext, setLockerExpansionContext] = useState(null);
  const [shippingFlowContext, setShippingFlowContext] = useState(null);
  const [shippingRequestPending, setShippingRequestPending] = useState(false);
  const [shippingError, setShippingError] = useState(null);
  const [showLockerFullModal, setShowLockerFullModal] = useState(false);
  const processedLockerExpansionSessionRef = useRef(null);

  // Sync locker decision with user pendingPrize
  useEffect(() => {
    if (!user) {
      if (lockerDecision) {
        setLockerDecision(null);
      }
      return;
    }
    if (user.pendingPrize) {
      setLockerDecision((current) => {
        if (current?.pendingPrize?.id === user.pendingPrize.id) {
          return current;
        }
        return {
          pendingPrize: user.pendingPrize,
          lockerItems: resolveLockerItems(user),
          maxSlots: Number.isFinite(user.maxLockerSlots) ? user.maxLockerSlots : BASE_LOCKER_SLOTS,
        };
      });
    } else if (lockerDecision) {
      setLockerDecision(null);
    }
  }, [user, lockerDecision]);

  // Handle locker expansion checkout polling
  useEffect(() => {
    if (
      !authReady ||
      !lockerExpansionContext ||
      lockerExpansionContext.status !== 'pending' ||
      !lockerExpansionContext.sessionId ||
      lockerExpansionContext.resolved
    ) {
      return;
    }

    let isActive = true;
    let timeoutId = null;
    const targetSessionId = lockerExpansionContext.sessionId;
    const maxAttempts = 6;

    const poll = async (attempt = 0) => {
      try {
        const result = await getLockerExpansionSessionStatus(targetSessionId);
        if (!isActive) {
          return;
        }

        if (result.status === 'completed' || result.status === 'success') {
          setLockerExpansionContext((current) => {
            if (!current || current.sessionId !== targetSessionId) {
              return current;
            }
            return {
              ...current,
              resolved: true,
              status: 'success',
              expansion: result.expansion || current.expansion || null,
              user: result.user || current.user || null,
            };
          });
          return;
        }

        if (result.status === 'pending' && attempt < maxAttempts) {
          timeoutId = setTimeout(() => poll(attempt + 1), 1500);
          return;
        }

        if (result.status === 'pending') {
          const message = 'We are still confirming your payment. Expansion will appear shortly.';
          setLockerExpansionContext((current) => {
            if (!current || current.sessionId !== targetSessionId) {
              return current;
            }
            return {
              ...current,
              status: 'pending',
              resolved: true,
              message,
            };
          });
          showToast({ message, tone: 'info' });
          return;
        }

        const fallbackMessage =
          result?.message ||
          'We could not confirm the payment. Please check your balance before retrying.';
        setLockerExpansionContext((current) => {
          if (!current || current.sessionId !== targetSessionId) {
            return current;
          }
          return {
            ...current,
            status: 'error',
            resolved: true,
            message: fallbackMessage,
          };
        });
        showToast({ message: fallbackMessage, tone: 'error' });
      } catch (error) {
        if (!isActive) {
          return;
        }
        console.error('Failed to confirm locker expansion session:', error);
        if (attempt < 3) {
          timeoutId = setTimeout(() => poll(attempt + 1), 1500);
          return;
        }
        const message =
          error?.response?.data?.error ||
          error?.message ||
          'Unable to verify expansion status. Please contact support if this persists.';
        setLockerExpansionContext((current) => {
          if (!current || current.sessionId !== targetSessionId) {
            return current;
          }
          return {
            ...current,
            status: 'error',
            resolved: true,
            message,
          };
        });
        showToast({ message, tone: 'error' });
      }
    };

    poll();

    return () => {
      isActive = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [lockerExpansionContext, authReady, showToast]);

  // Process successful locker expansion
  useEffect(() => {
    if (
      !lockerExpansionContext ||
      lockerExpansionContext.status !== 'success' ||
      !lockerExpansionContext.resolved
    ) {
      return;
    }

    const sessionKey =
      lockerExpansionContext.sessionId ||
      lockerExpansionContext.id ||
      lockerExpansionContext.expansion?.id ||
      null;

    if (
      !sessionKey ||
      processedLockerExpansionSessionRef.current === sessionKey
    ) {
      return;
    }

    processedLockerExpansionSessionRef.current = sessionKey;

    // CHANGED: Log analytics event for locker expansion
    logLockerExpansion();

    refreshUser?.().catch(() => {});
    showToast({
      message: 'Locker expanded! You have 4 extra slots now.',
      tone: 'success',
    });
  }, [lockerExpansionContext, refreshUser, showToast]);

  const expandLocker = useCallback(async () => {
    if (!user) {
      requireLogin?.('Sign in to expand your locker.');
      return;
    }
    setLockerActionPending(true);
    try {
      const result = await createLockerExpansionSession();
      if (result?.checkoutUrl) {
        window.location.assign(result.checkoutUrl);
        return;
      }
      if (result?.sessionId) {
        setLockerExpansionContext({
          status: 'pending',
          sessionId: result.sessionId,
          id: Date.now(),
        });
        return;
      }
      throw new Error('Locker expansion checkout did not return a redirect URL.');
    } catch (err) {
      console.error('Locker expansion checkout failed:', err);
      const message =
        err.response?.data?.error ||
        err.message ||
        'Unable to open locker expansion checkout right now. Please try again later.';
      showToast({ message, tone: 'error' });
    } finally {
      setLockerActionPending(false);
    }
  }, [user, requireLogin, showToast]);

  const startShippingFlow = useCallback(
    (origin = 'manual') => {
      if (!user) {
        requireLogin?.();
        return;
      }
      const items = resolveLockerItems(user);
      if (items.length === 0) {
        showToast({ message: 'Your locker is empty.', tone: 'error' });
        return;
      }
      setShippingFlowContext({
        origin,
        items,
        pendingPrize: user.pendingPrize || null,
        maxLockerSlots: Number.isFinite(user?.maxLockerSlots)
          ? user.maxLockerSlots
          : BASE_LOCKER_SLOTS,
        shippingAddress: user.shippingAddress || null,
        id: Date.now(),
      });
    },
    [user, requireLogin, showToast],
  );

  const shipLockerNow = useCallback(() => {
    startShippingFlow('forced');
  }, [startShippingFlow]);

  const confirmShipment = useCallback(
    async ({ itemIds = [], address, shippingFee = 0 } = {}) => {
      if (!user) {
        requireLogin?.();
        return;
      }
      setShippingRequestPending(true);
      setShippingError(null);
      try {
        const payload = {
          itemIds: Array.isArray(itemIds) ? itemIds : [],
          address,
        };

        const fee = Number(shippingFee) || 0;
        if (fee > 0) {
          const { createShippingCheckout } = await import('../../services/api');
          const result = await createShippingCheckout(payload);
          setShippingFlowContext(null);
          if (result?.checkoutUrl) {
            window.location.assign(result.checkoutUrl);
            return;
          }
          throw new Error('Shipping checkout session did not return a redirect URL.');
        }

        const { shipLockerContents } = await import('../../services/api');
        const result = await shipLockerContents(payload);
        if (result?.user) {
          await refreshUser?.();
        }
        // CHANGED: Log analytics event for locker shipment (free shipping)
        const itemCount = Array.isArray(itemIds) ? itemIds.length : 0;
        logLockerShipment(itemCount, 0);
        setShippingFlowContext(null);
        showToast({
          message: 'Shipment confirmed! Locker space is open again.',
          tone: 'success',
        });
      } catch (err) {
        console.error('Failed to ship locker contents:', err);
        const message =
          err.response?.data?.error ||
          err.message ||
          'Shipment failed. Please try again later.';
        setShippingError(message);
      } finally {
        setShippingRequestPending(false);
      }
    },
    [user, requireLogin, refreshUser, showToast],
  );

  const closeShippingFlow = useCallback(() => {
    if (shippingRequestPending) {
      return;
    }
    setShippingFlowContext(null);
    setShippingError(null);
  }, [shippingRequestPending]);

  // CHANGED: add functions to open/close locker full modal from LockerPage
  const openLockerFullModal = useCallback(() => {
    if (user?.pendingPrize || lockerDecision?.pendingPrize) {
      setShowLockerFullModal(true);
    }
  }, [user?.pendingPrize, lockerDecision?.pendingPrize]);

  const closeLockerFullModal = useCallback(() => {
    setShowLockerFullModal(false);
  }, []);

  // CHANGED: close modal when locker decision is cleared
  useEffect(() => {
    if (!lockerDecision && showLockerFullModal) {
      setShowLockerFullModal(false);
    }
  }, [lockerDecision, showLockerFullModal]);

  const value = useMemo(
    () => ({
      lockerDecision,
      lockerGainEvent,
      lockerActionPending,
      lockerExpansionContext,
      shippingFlowContext,
      shippingRequestPending,
      shippingError,
      showLockerFullModal,
      setLockerDecision,
      setLockerGainEvent,
      expandLocker,
      startShippingFlow,
      shipLockerNow,
      confirmShipment,
      closeShippingFlow,
      openLockerFullModal,
      closeLockerFullModal,
      resolveLockerItems,
    }),
    [
      lockerDecision,
      lockerGainEvent,
      lockerActionPending,
      lockerExpansionContext,
      shippingFlowContext,
      shippingRequestPending,
      shippingError,
      showLockerFullModal,
      expandLocker,
      startShippingFlow,
      shipLockerNow,
      confirmShipment,
      closeShippingFlow,
      openLockerFullModal,
      closeLockerFullModal,
    ],
  );

  return <LockerContext.Provider value={value}>{children}</LockerContext.Provider>;
};

export const useLocker = () => {
  return useContext(LockerContext);
};

export default LockerProvider;
